import { useCallback, useEffect, useState } from 'react';

import { logger } from '@garnish/logger';

import type { UseQueryResponse } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Re-executes {useQuery} with {input} every {interval} ms, scheduling the next
 * run only once the current response has stopped fetching.
 */
export const useUrqlPolling = <T,>(
  interval: number,
  input: T,
  useQuery: (input: T) => UseQueryResponse,
  skip?: boolean,
) => {
  const [response, reexecuteQuery] = useQuery(input);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleReExecute = useCallback(() => {
    reexecuteQuery({ requestPolicy: 'network-only' });
  }, [reexecuteQuery]);

  const scheduleReExecute = useCallback(() => {
    if (skip) {
      return;
    }

    if (response.fetching) {
      return;
    }

    const timerId = setTimeout(handleReExecute, interval);

    return () => {
      clearTimeout(timerId);
    };
  }, [response.fetching, interval, skip, handleReExecute]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(scheduleReExecute, [scheduleReExecute]);

  return { response, reexecuteQuery: handleReExecute };
};

/**
 * Calls {pollingFunction} every {interval} ms, scheduling the next call only
 * once the current one has settled.
 *
 * {pollingFunction} must be referentially stable: a new identity restarts the
 * interval, so a caller that re-renders faster than {interval} never polls.
 */
export const useFunctionPolling = (
  interval: number,
  pollingFunction: PollingFunction,
  skip?: boolean,
) => {
  //
  // ─── State ─────────────────────────────────────────────────────────────────

  const [isPolling, setIsPolling] = useState(false);

  // A synchronous poll settles in the same render batch that set `isPolling`,
  // so `isPolling` never changes and only this count re-arms the next timeout.
  const [settledPollCount, setSettledPollCount] = useState(0);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handlePollSettled = useCallback(() => {
    setIsPolling(false);
    setSettledPollCount(incrementCount);
  }, []);

  const runPoll = useCallback(() => {
    setIsPolling(true);

    void Promise.resolve(pollingFunction()).catch(logPollingError).finally(handlePollSettled);
  }, [pollingFunction, handlePollSettled]);

  const schedulePoll = useCallback(() => {
    if (skip) {
      return;
    }

    if (isPolling) {
      return;
    }

    const timerId = setTimeout(runPoll, interval);

    return () => {
      clearTimeout(timerId);
    };
  }, [interval, skip, isPolling, runPoll]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(schedulePoll, [schedulePoll, settledPollCount]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function logPollingError(error: unknown) {
  logger.error('[useFunctionPolling]', error);
}

function incrementCount(count: number) {
  return count + 1;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type PollingFunction = (() => Promise<unknown>) | (() => void);
